import {isLargeScreenAndUp, isMediumScreenOnly} from '../../modules/breakpoint.js';
import {bar} from '../../components/scheduleBar/scheduleBar.js';
import {t} from '../../localization/translate.js';

/**
 * Initializes expansion feature for course cards sections
 */
function initCourseCardsSections() {
    const cardsSections = [...document.querySelectorAll('[data-show-limited-cards]')];
    cardsSections.forEach((section) => setOverlayVisibility(section));

    // Recalculate on window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            cardsSections.forEach((section) => {
                // Reset any inline styles and recalculate
                const cardsContainer = section.querySelector('.js-cards-list');
                if (cardsContainer && !cardsContainer.classList.contains('js-expanded')) {
                    cardsContainer.style.maxHeight = '';
                    setOverlayVisibility(section);
                }
            });
        }, 250);
    });
}

/**
 * Sets the visibility of expansion button and overlay if cards exceed 2 rows
 * @param {HTMLElement} cardSection
 */
function setOverlayVisibility(cardSection) {
    const cardsContainer = cardSection.querySelector('.js-cards-list');
    if (!cardsContainer) return;

    const totalCards = cardsContainer.querySelectorAll('.js-card');
    const toggleExpansionButton = cardSection.querySelector('.js-expand-button');
    const overlay = cardsContainer.querySelector('.js-overlay');

    // Not every section with `data-show-limited-cards` has expandable controls.
    if (!toggleExpansionButton || !overlay) {
        return;
    }

    // Calculate cards per row based on screen size and determine if we need expansion
    let maxVisibleCards = 2; // Default to 2 rows

    if (isLargeScreenAndUp()) {
        maxVisibleCards = 8; // 2 rows × 4 cards
    } else if (isMediumScreenOnly()) {
        maxVisibleCards = 6; // 2 rows × 3 cards
    } else {

        maxVisibleCards = 4; // 2 rows × 2 cards
    }

    if (totalCards.length > maxVisibleCards && toggleExpansionButton && overlay) {
        addExpandableOption(cardsContainer, toggleExpansionButton, overlay, maxVisibleCards);
    }
}

/**
 * Show expand button and setup expansion functionality
 * @param {HTMLElement} cardsContainer
 * @param {HTMLElement} toggleExpansionButton
 * @param {HTMLElement} overlay
 * @param {number} maxVisibleCards
 */
function addExpandableOption(cardsContainer, toggleExpansionButton, overlay, maxVisibleCards) {
    // Show the expand button
    toggleExpansionButton.classList.remove('hidden');

    // Set initial collapsed state
    setCollapsedState(cardsContainer, toggleExpansionButton, overlay, maxVisibleCards);

    // Add click event listener
    toggleExpansionButton.addEventListener('click', () => {
        const isExpanded = cardsContainer.classList.contains('js-expanded');
        if (isExpanded) {
            setCollapsedState(cardsContainer, toggleExpansionButton, overlay, maxVisibleCards);
        } else {
            setExpandedState(cardsContainer, toggleExpansionButton, overlay);
        }
    });
}

/**
 * Set the collapsed state with overlay and limited height
 * @param {HTMLElement} cardsContainer
 * @param {HTMLElement} toggleExpansionButton
 * @param {HTMLElement} overlay
 * @param {number} maxVisibleCards
 */
function setCollapsedState(cardsContainer, toggleExpansionButton, overlay, maxVisibleCards) {
    const cards = cardsContainer.querySelectorAll('.js-card');
    const buttonText = toggleExpansionButton.querySelector('.js-button-text');
    const chevron = toggleExpansionButton.querySelector('.js-chevron');

    // Calculate the height needed for the visible cards
    if (cards.length > 0) {
        const firstCard = cards[0];
        const cardHeight = firstCard.offsetHeight;
        const gap = 32;

        Math.ceil(maxVisibleCards / 4);

        // Set container max height to show 1.75 rows (first row + 75% of second row)
        const oneAndThreeQuarterRowsHeight = cardHeight + gap + (cardHeight * 0.75);
        cardsContainer.style.maxHeight = `${oneAndThreeQuarterRowsHeight}px`;

        // Position overlay to cover only the bottom 25% of the second row
        const overlayHeight = cardHeight * 0.4; // Cover 25% of card plus some fade effect
        overlay.style.height = `${overlayHeight}px`;
        overlay.style.top = `${oneAndThreeQuarterRowsHeight - overlayHeight}px`;
    }

    // Show overlay with higher z-index to cover buttons
    overlay.classList.remove('opacity-0');
    overlay.classList.add('opacity-100');
    overlay.style.zIndex = '20';

    // Update button text and icon
    if (buttonText) {
        buttonText.textContent = t('More');
    }

    if (chevron) {
        chevron.classList.remove('rotate-180');
    }

    // Remove expanded class
    cardsContainer.classList.remove('js-expanded');
}

/**
 * Set the expanded state without overlay and full height
 * @param {HTMLElement} cardsContainer
 * @param {HTMLElement} toggleExpansionButton
 * @param {HTMLElement} overlay
 */
function setExpandedState(cardsContainer, toggleExpansionButton, overlay) {
    const buttonText = toggleExpansionButton.querySelector('.js-button-text');
    const chevron = toggleExpansionButton.querySelector('.js-chevron');

    // Remove height restriction
    cardsContainer.style.maxHeight = 'none';

    // Hide overlay and reset its positioning
    overlay.classList.remove('opacity-100');
    overlay.classList.add('opacity-0');
    overlay.style.height = '';
    overlay.style.top = '';
    overlay.style.zIndex = '';

    // Update button text and icon
    if (buttonText) {
        buttonText.textContent = t('Show fewer');
    }

    if (chevron) {
        chevron.classList.add('rotate-180');
    }

    // Add expanded class
    cardsContainer.classList.add('js-expanded');
}

/**
 * Initialize page interactions
 */
function initPageInteractions() {
    initCourseCardsSections();
    bar.runCountdowns();
}

document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', initPageInteractions)
    : initPageInteractions();
